<template>
<div>
    <CCol sm="12">
        <CCard>
            <CCardBody class="d-flex flex-column">
                <div class="d-flex justify-content-end align-items-start">
                    <!-- Title -->
                    <p class="flex-grow-1">{{ $t('product') }}</p>
                    <!-- Language dropdown -->
                    <LanguageDropdown v-model="currentLanguage"></LanguageDropdown>
                </div>
                <!-- Form -->
                <div class="d-flex flex-wrap gap-1">
                    <!-- ECommerce -->
                    <div role="group" class="form-group">
                        <label> {{ $t('eCommerce') }}</label>
                        <CInputCheckbox :checked='product.eCommerce' @update:checked="product.eCommerce = !product.eCommerce" :invalid-feedback="errorOutput['ECommerce']" :is-valid="genericValidator(product.eCommerce, attemptedSubmit) && errorValidator('ECommerce')"></CInputCheckbox>
                    </div>
                    <!-- InStock -->
                    <div v-show="product.eCommerce" role="group" class="form-group">
                        <label> {{ $t('inStock') }}</label>
                        <CInputCheckbox :checked='product.inStock' @update:checked="product.inStock = !product.inStock" :invalid-feedback="errorOutput['InStock']" :is-valid="genericValidator(product.inStock, attemptedSubmit) && errorValidator('InStock')"></CInputCheckbox>
                    </div>
                    <!-- highlighted -->
                    <div role="group" class="form-group mx-3">
                        <label> {{ $t('highlighted') }}</label>
                        <!-- <CInputCheckbox :checked='product.highlighted' @update:checked="product.highlighted = !product.highlighted" :invalid-feedback="errorOutput['Highlighted']" :is-valid="genericValidator(product.highlighted, attemptedSubmit) && errorValidator('Highlighted')"></CInputCheckbox> -->
                        <CInput type="number" v-model="product.highlighted" :invalid-feedback="errorOutput['Highlighted']" :is-valid="errorValidator('Highlighted')"></CInput>
                    </div>
                    <!-- ShowFirst -->
                    <div role="group" class="form-group mx-3">
                        <label> {{ $t('showFirst') }}</label>
                        <!-- <CInputCheckbox :checked='product.showFirst' @update:checked="product.showFirst = !product.showFirst" :invalid-feedback="errorOutput['ShowFirst']" :is-valid="genericValidator(product.showFirst, attemptedSubmit) && errorValidator('ShowFirst')"></CInputCheckbox> -->
                        <CInput type="number" v-model="product.showFirst" :invalid-feedback="errorOutput['ShowFirst']" :is-valid="errorValidator('ShowFirst')"></CInput>
                    </div>
                    <!-- ShowOnActiveBrandsPro -->
                    <div role="group" class="form-group mx-3">
                        <label> {{ $t('showOnActiveBrandsPro') }}</label>
                        <CInputCheckbox :checked='product.showOnActiveBrandsPro' @update:checked="product.showOnActiveBrandsPro = !product.showOnActiveBrandsPro"></CInputCheckbox>
                    </div>
                    <!-- Names -->
                    <div role="group" class="form-group flex-grow-1 mx-3">
                        <label class=""> {{ $t('nameMandatory') }}</label>
                        <CInput type="text" v-model="product.names[currentLanguage.key]" :invalid-feedback="errorOutput['Names']" :is-valid="genericValidator(product.names, attemptedSubmit) && errorValidator('Names')"></CInput>
                    </div>
                </div>
                <div class="d-flex flex-wrap mb-3">
                    <!-- Galleries column -->
                    <div class="d-flex flex-column col-12 col-lg-6 pe-lg-3">
                        <!-- Default gallery -->
                        <div class="d-flex flex-column">
                            <label class="font-weight-bold mb-1">{{ $t('defaultGallery') }}</label>
                            <GalleryPicker class="gallery-picker" v-model="product.files" :ratioWidth="16" :ratioHeight="9" v-bind:class="{ 'is-invalid': errorValidator('Files') === false }" :max="0" :allowReorder="true"></GalleryPicker>
                            <div v-if="errorValidator('Files') === false" class="invalid-feedback d-block">{{errorOutput['Files']}}</div>
                        </div>
                        <!-- ActiveBrands Pro gallery (optional, only when toggle is on) -->
                        <transition name="fade">
                            <div v-if="product.showOnActiveBrandsPro" class="d-flex flex-column mt-3">
                                <label class="font-weight-bold mb-1">{{ $t('activeBrandsProGallery') }}</label>
                                <small class="text-muted mb-1">{{ $t('activeBrandsProGalleryHint') }}</small>
                                <GalleryPicker class="gallery-picker" v-model="product.activeBrandsProFiles" :ratioWidth="16" :ratioHeight="9" v-bind:class="{ 'is-invalid': errorValidator('ActiveBrandsProFiles') === false }" :max="0" :allowReorder="true"></GalleryPicker>
                                <div v-if="errorValidator('ActiveBrandsProFiles') === false" class="invalid-feedback d-block">{{errorOutput['ActiveBrandsProFiles']}}</div>
                            </div>
                        </transition>
                    </div>
                    <div class="d-flex flex-column col-12 col-lg-6">
                        <!-- Sku -->
                        <div role="group" class="form-group flex-grow-1">
                            <label class=""> {{ $t('skuMandatory') }}</label>
                            <CInput type="text" v-model="product.sku" :invalid-feedback="errorOutput['SKU']" :is-valid="genericValidator(product.sku, attemptedSubmit) && errorValidator('SKU')"></CInput>
                        </div>
                        <!-- Category -->
                        <div role="group" class="form-group flex-grow-1">
                            <label class=""> {{ $t('categoriesMandatory') }}</label>
                            <GroupedOptionsDropdown v-model="product.categories" :optionGroups="categoriesOptions" :title="$t('categories')" :index="0" :is-valid="genericValidator(product.categories, attemptedSubmit) && errorValidator('Categories')" :invalid-feedback="errorOutput['Categories']"></GroupedOptionsDropdown>
                        </div>
                        <!-- Tags -->
                        <div role="group" class="form-group flex-grow-1">
                            <label class=""> {{ $t('tagsMandatory') }}</label>
                            <vue-tags-input v-model="tag" :tags="product.tags[currentLanguage.key]" @tags-changed="newTags => product.tags[currentLanguage.key] = newTags.map(x => x.text)"  v-bind:class="{'is-invalid': genericValidator(product.tags[currentLanguage.key], attemptedSubmit) == false || errorValidator('Tags') }" class="flex-grow-1"/>
                            <div v-if="genericValidator(product.tags[currentLanguage.key], attemptedSubmit) === false" class="invalid-feedback d-block">{{errorOutput['Tags']}}</div>
                        </div>
                        <!-- VideoUrl -->
                        <div role="group" class="form-group flex-grow-1">
                            <label class=""> {{ $t('videoUrlMandatory') }}</label>
                            <CInput type="text" v-model="product.videoUrl" :invalid-feedback="errorOutput['VideoUrl']" :is-valid="errorValidator('VideoUrl')"></CInput>
                        </div>
                        <!-- Datasheets -->
                        <div v-for="language in languages" :key="language.key" role="group" class="form-group flex-grow-1 mb-0">
                            <CustomPicker v-show="currentLanguage && currentLanguage.key == language.key" v-model="product.datasheets[currentLanguage.key]" :title="$t('dataSheetsFileMandatory')" v-bind:class="{'is-invalid': errorValidator('Datasheets') }"></CustomPicker>
                            <!-- <CustomPicker v-show="currentLanguage && currentLanguage.key == language.key" v-model="product.datasheets[currentLanguage.key]" :title="$t('dataSheetsFileMandatory')" v-bind:class="{'is-invalid': genericValidator(product.datasheets, attemptedSubmit) == false || errorValidator('Datasheets') }" :invalidFeedback="errorOutput['Datasheets']"></CustomPicker> -->
                        </div>
                    </div>
                </div>
                <div v-if="product.eCommerce" class="d-flex gap-1 flex-grow-1">
                    <!-- Model -->
                    <div role="group" class="form-group">
                        <label class=""> {{ $t('modelMandatory') }}</label>
                        <CInputCheckbox :checked='product.model' @update:checked="product.model = !product.model" :invalid-feedback="errorOutput['Model']" :is-valid="errorValidator('Model')"></CInputCheckbox>
                    </div>
                    <!-- ModelFile -->
                    <div role="group" class="form-group flex-grow-1 d-flex flex-wrap" :class="{'disabled': !product.model}">
                        <!-- addProduct -->
                        <CustomPicker class="col-6" v-model="product.modelFile" :title="$t('modelFileMandatory')" mime="file/obj" accept=".obj" v-bind:class="{'is-invalid': (product.model && (genericValidator(product.modelFile, attemptedSubmit) === false) || errorValidator('ModelFile') === false)}"></CustomPicker>
                        <!-- addMTL -->
                        <CustomPicker class="col-6" v-model="product.mtlFile" :title="$t('mtlFileMandatory')" mime="file/mtl" accept=".mtl" v-bind:class="{'is-invalid': (product.model && (genericValidator(product.mtlFile, attemptedSubmit) === false) || errorValidator('mtlFile') === false)}"></CustomPicker>
                        <!-- addSVG -->
                        <CustomPicker class="col-6" v-model="product.svgFile" :title="$t('svgFileMandatory')" mime="file/svg" accept=".svg" v-bind:class="{'is-invalid': (product.model && (genericValidator(product.svgFile, attemptedSubmit) === false) || errorValidator('svgFile') === false)}"></CustomPicker>
                        <!-- PaletteId (v-if solves a problem on the palette id not being shown on loading) -->
                        <div v-if="palettes.length" role="group" class="form-group col-6">
                            <label class=""> {{ $t('paletteIdMandatory') }}</label>
                            <SingleOptionDropdown v-model="product.paletteId" :options="palettes" :title="$t('paletteId')" :index="0" :is-valid="!product.model || genericValidator(product.paletteId, attemptedSubmit) && errorValidator('PaletteId')" :invalid-feedback="errorOutput['PaletteId']"></SingleOptionDropdown>
                        </div>
                    </div>
                </div>
                <div v-if="product.eCommerce" class="d-flex gap-1 flex-grow-1">
                    <!-- Price -->
                    <div role="group" class="form-group flex-grow-1">
                        <div class="d-flex gap-1">
                            <label> {{ $t('priceMandatory') }}</label>
                            <CButton type="button" class="btn btn-secondary" @click="addPrice">{{ $t('addQuantityDependentPrice') }}</CButton>
                        </div>
                        <CInput type="number" v-model="product.price" :is-valid="genericValidator(product.price, attemptedSubmit) && errorValidator('Price')"></CInput>
                    </div>
                    <!-- Tax -->
                    <div role="group" class="form-group flex-grow-1">
                        <label class=""> {{ $t('taxMandatory') }}</label>
                        <SingleOptionDropdown v-model="product.tax" :options="taxOptions" :title="$t('tax')" :index="0" :is-valid="genericValidator(product.tax, attemptedSubmit) && errorValidator('Tax')" :invalid-feedback="errorOutput['Tax']"></SingleOptionDropdown>
                    </div>
                </div>
                <!-- Prices -->
                <div v-if="product.eCommerce" class="d-flex flex-column flex-grow-1">
                    <div class="d-flex gap-1 align-items-center" v-for="(price, index) in product.prices" :key="index">
                        <CButton type="button" class="btn btn-secondary" @click="removePrice(index)">{{ $t('remove') }}</CButton>
                        <div role="group" class="form-group">
                            <label class=""> {{ $t('minQuantity') }}</label>
                            <CInput type="number" v-model="product.prices[index].minQuantity" :is-valid="genericValidator(product.prices, attemptedSubmit) && errorValidator('Prices')"></CInput>
                        </div>
                        <div role="group" class="form-group">
                            <label class=""> {{ $t('maxQuantity') }}</label>
                            <CInput type="number" v-model="product.prices[index].maxQuantity" :is-valid="genericValidator(product.prices, attemptedSubmit) && errorValidator('Prices')"></CInput>
                        </div>
                        <div role="group" class="form-group flex-grow-1">
                            <label class=""> {{ $t('price') }}</label>
                            <CInput type="number" v-model="product.prices[index].price" :is-valid="genericValidator(product.prices, attemptedSubmit) && errorValidator('Prices')"></CInput>
                        </div>
                    </div>
                </div>
                <!-- Shippings -->
                <div v-if="product.eCommerce" class="d-flex flex-column">
                    <div v-if="!normalShipping" class="d-flex gap-1">
                        <CButton type="button" class="btn btn-secondary" @click="ensureNormalShipping">{{ $t('addNormalShipping') }}</CButton>
                    </div>
                    <div v-if="normalShipping" class="d-flex flex-column">
                    <!-- Normal shipping -->
                    <div class="d-flex gap-1">
                        <label class="font-weight-bold"> {{ $t('normalShipping') }}</label>
                        <CButton v-if="!expressShipping" type="button" class="btn btn-secondary" @click="addExpress">{{ $t('addExpressShipping') }}</CButton>
                    </div>
                    <div class="d-flex gap-1">
                        <div role="group" class="form-group flex-grow-1">
                            <label class=""> {{ $t('daysMandatory') }}</label>
                            <CInput type="number" v-model="normalShipping.days" :is-valid="genericValidator(normalShipping.days, attemptedSubmit) && errorValidator('ShippingsError')" :invalid-feedback="errorOutput['ShippingsError']"></CInput>
                        </div>
                        <div role="group" class="form-group flex-grow-1">
                            <label class=""> {{ $t('priceMandatory') }}</label>
                            <CInput type="number" v-model="normalShipping.price" :is-valid="genericValidator(normalShipping.price, attemptedSubmit) && errorValidator('ShippingsError')" :invalid-feedback="errorOutput['ShippingsError']"></CInput>
                        </div>                        
                    </div>
                    <!-- Express shipping -->
                    <div v-if="expressShipping" class="d-flex gap-1">
                        <label class="font-weight-bold"> {{ $t('expressShipping') }}</label>
                        <CButton type="button" class="btn btn-secondary" @click="removeExpress">{{ $t('remove') }}</CButton>
                    </div>
                    <div v-if="expressShipping" class="d-flex gap-1">
                        <div role="group" class="form-group flex-grow-1">
                            <label class=""> {{ $t('daysMandatory') }}</label>
                            <CInput type="number" v-model="expressShipping.days" :is-valid="genericValidator(expressShipping.days, attemptedSubmit) && errorValidator('ShippingsError')" :invalid-feedback="errorOutput['ShippingsError']"></CInput>
                        </div>
                        <div role="group" class="form-group flex-grow-1">
                            <label class=""> {{ $t('priceMandatory') }}</label>
                            <CInput type="number" v-model="expressShipping.price" :is-valid="genericValidator(expressShipping.price, attemptedSubmit) && errorValidator('ShippingsError')"></CInput>
                        </div>                        
                    </div>
                    </div>
                </div>
                <!-- ShortDescriptions -->
                <div role="group" class="form-group">
                    <label class=""> {{ $t('shortDescriptionMandatory') }}</label>
                    <vue-editor v-model="product.shortDescriptions[currentLanguage.key]" v-bind:class="{'is-invalid': genericValidator(product.shortDescriptions, attemptedSubmit) == false || errorValidator('ShortDescriptions') }"></vue-editor>
                    <div v-if="errorOutput['ShortDescriptions']" class="invalid-feedback">{{errorOutput['ShortDescriptions']}}</div>
                </div>
                <!-- LongDescriptions -->
                <div role="group" class="form-group">
                    <label class=""> {{ $t('longDescriptionMandatory') }}</label>
                    <vue-editor v-model="product.longDescriptions[currentLanguage.key]" v-bind:class="{'is-invalid': genericValidator(product.longDescriptions, attemptedSubmit) == false || errorValidator('LongDescriptions') }"></vue-editor>
                    <div v-if="errorOutput['LongDescriptions']" class="invalid-feedback">{{errorOutput['LongDescriptions']}}</div>
                </div>
                <!-- Dimensions -->
                <div class="d-flex flex-wrap justify-content-between gap-1">
                    <!-- Weight -->
                    <div role="group" class="form-group flex-grow-1">
                        <label class=""> {{ $t('weightMandatory') }}</label>
                        <CInput type="number" v-model="product.weight" :invalid-feedback="errorOutput['Weight']" :is-valid="genericValidator(product.weight, attemptedSubmit) && errorValidator('Weight')"></CInput>
                    </div>
                    <!-- Length -->
                    <div role="group" class="form-group flex-grow-1">
                        <label class=""> {{ $t('lengthMandatory') }}</label>
                        <CInput type="number" v-model="product.length" :invalid-feedback="errorOutput['Length']" :is-valid="genericValidator(product.length, attemptedSubmit) && errorValidator('Length')"></CInput>
                    </div>
                    <!-- Width -->
                    <div role="group" class="form-group flex-grow-1">
                        <label class=""> {{ $t('widthMandatory') }}</label>
                        <CInput type="number" v-model="product.width" :invalid-feedback="errorOutput['Width']" :is-valid="genericValidator(product.width, attemptedSubmit) && errorValidator('Width')"></CInput>
                    </div>
                    <!-- Height -->
                    <div role="group" class="form-group flex-grow-1">
                        <label class=""> {{ $t('heightMandatory') }}</label>
                        <CInput type="number" v-model="product.height" :invalid-feedback="errorOutput['Height']" :is-valid="genericValidator(product.height, attemptedSubmit) && errorValidator('Height')"></CInput>
                    </div>
                </div>
                <div class="d-flex flex-wrap justify-content-between gap-1">
                    <!-- Box Sizes -->
                    <div role="group" class="form-group flex-grow-1">
                        <label class=""> {{ $t('boxSize') }}</label>
                        <CInput type="text" v-model="product.packageDimensions[currentLanguage.key]" :invalid-feedback="errorOutput['PackageDimensions']" :is-valid="genericValidator(product.packageDimensions, attemptedSubmit) && errorValidator('PackageDimensions')"></CInput>
                    </div>
                    <!-- Number of Motors -->
                    <div role="group" class="form-group flex-grow-1">
                        <label class=""> {{ $t('numberOfMotors') }}</label>
                        <CInput type="text" v-model="product.motors" :invalid-feedback="errorOutput['Motors']" :is-valid="errorValidator('Motors')"></CInput>
                    </div>
                    <!-- OtherDimensions -->
                    <div role="group" class="form-group flex-grow-1">
                        <label class=""> {{ $t('otherDimensions') }}</label>
                        <CInput type="text" v-model="product.otherDimensions" :invalid-feedback="errorOutput['OtherDimensions']" :is-valid="errorValidator('OtherDimensions')"></CInput>
                    </div>
                </div>
                <!-- Action button + submitting -->  
                <CSpinner v-if="submitting" variant="success" small class="align-self-end"></CSpinner>
                <CButton v-else type="button" class="btn btn-primary align-self-end" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
                <!-- Loading -->
                <FormLoading v-if="loading"></FormLoading>
            </CCardBody>
        </CCard>
    </CCol>
    <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup" v-model="showConfirmationPopup"></ConfirmationPopUp>
</div>
</template>
 
<script>
import { mapState, mapActions } from 'vuex'
import router from '@/router/index';

import { API_HELPER } from "@/helpers/api.js";
import { deepClone } from "@/helpers/utils.js";
import { CONSTANTS } from '@/helpers/consts';
import { genericValidator, errorValidator } from "@/helpers/utils.js";

import InputGetList from "@/classes/InputGetList";
import Product from "@/classes/Product";
import Price from "@/classes/Price";

import LanguageDropdown from '@/controls/LanguageDropdown.vue'
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import GroupedOptionsDropdown from '@/controls/GroupedOptionsDropdown.vue'
import SingleOptionDropdown from '@/controls/SingleOptionDropdown.vue'
import GalleryPicker from '@/controls/GalleryPicker.vue'
import CustomPicker from '@/controls/CustomPicker.vue'
import { VueEditor } from "vue2-editor";
import FormLoading from '@/controls/FormLoading.vue'
import VueTagsInput from '@johmun/vue-tags-input';

const NORMAL_SHIPPING = "Normal";
const EXPRESS_SHIPPING = "Express";

  export default {
    components: { LanguageDropdown, ConfirmationPopUp, SingleOptionDropdown, VueEditor, GalleryPicker, GroupedOptionsDropdown, FormLoading, CustomPicker, VueTagsInput },
    props:{    
        baseProduct:
        {
            type: Object,
            default: null,
        },
        baseName:
        {
            type: String,
            default: null,
        },
        loading:
        {
            type: Boolean,
            default: false,
        },
    },
    data()
    {
        return {
            attemptedSubmit: false,
            languages: CONSTANTS.supportedLanguages,
            currentLanguage: CONSTANTS.supportedLanguages[0],
            product: new Product({}, {}, {}, {}, {}, {}, [], [], [], null),
            submitting: false,
            showConfirmationPopup: false,
            confirmationMessage:"",
            taxOptions: [{label: this.$t("free"), key: "Free"}, {label: this.$t("reduced"), key: "Reduced"}, {label: this.$t("intermediate"), key: "Intermediate"}, {label: this.$t("normal"), key: "Normal"}],
            tag: ''
        }
    },
    mounted: function()
    {
        this.resetProduct();
        this.getActiveCategories();
        this.getInflateCategories();
        this.getAllPalettes();
    },
    computed:
    {
        ...mapState('products', ['putProductOutput']),
        ...mapState('products', ['postProductOutput']),
        ...mapState('palettes', ['getPalettesOutput']),
        ...mapState('categories', ['getInflateCategoriesOutput']),
        ...mapState('categories', ['getActiveCategoriesOutput']),
        ...mapState({ 
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        palettesInfo: function() {
            return this.getPalettesOutput && this.getPalettesOutput.data ? this.getPalettesOutput.data : null;
        },
        palettes: function()
        {
            var result = [];

            if(this.palettesInfo)
            {
                this.palettesInfo.forEach(palette => 
                {
                    result.push({key: palette.id, label: palette.name });
                });
            }

            return result;
        }, 
        categoriesInfo: function() {
            return this.getInflateCategoriesOutput && this.getInflateCategoriesOutput.data && this.getActiveCategoriesOutput && this.getActiveCategoriesOutput.data ? this.getInflateCategoriesOutput.data.concat(this.getActiveCategoriesOutput.data) : null;
        },
        categoriesOptions: function()
        {
            var result = [];

            if(this.getInflateCategoriesOutput && this.getInflateCategoriesOutput.data && this.getActiveCategoriesOutput && this.getActiveCategoriesOutput.data)
            {
                var activeCategories = [];
                this.getActiveCategoriesOutput.data.forEach(category => 
                {
                    var options = category.children.map(function(child) { return {key: child.id, label: child.name} });
                    if(options.length)
                    {
                        activeCategories.push({key: category.id, label: category.name, options: options });
                    }
                });
                result.push({label: this.$t('activeBrands'), options: activeCategories });

                var inflateCategories = [];
                this.getInflateCategoriesOutput.data.forEach(category => 
                {
                    var options = category.children.map(function(child) { return {key: child.id, label: child.name} });
                    if(options.length)
                    {
                        inflateCategories.push({key: category.id, label: category.name, options: options });
                    }
                });
                result.push({label: this.$t('inflateYourImagination'), options: inflateCategories });
            }

            return result;
        },          
        errorOutput: function()
        {
            var result = this.alertOutput && this.alertOutput.Errors ? this.alertOutput.Errors : [];
            
            // Validate multi language fields
            for (const [, value] of Object.entries(this.product.names)) { if(!value) { result['Names'] = this.$i18n.t("validateBothTranslations"); break; } }
            for (const [, value] of Object.entries(this.product.shortDescriptions)) { if(!value) { result['ShortDescriptions'] = this.$i18n.t("validateBothTranslations"); break; } }
            for (const [, value] of Object.entries(this.product.longDescriptions)) { if(!value) { result['LongDescriptions'] = this.$i18n.t("validateBothTranslations"); break; } }
            for (const [, value] of Object.entries(this.product.packageDimensions)) { if(!value) { result['PackageDimensions'] = this.$i18n.t("validateBothTranslations"); break; } }
            // for (const [key, value] of Object.entries(this.product.datasheets)) { if(!value) { result['DataSheets'] = this.$i18n.t("validateBothTranslations"); break; }; }
            for (const [, value] of Object.entries(this.product.tags)) { if(!value || !value.length) { result['Tags'] = this.$i18n.t("validateBothTranslations"); break; } }

            return result;
        },
        name: function () {
            return this.baseProduct || this.baseName ? (this.baseProduct ? this.baseProduct.name : this.baseName) : null;
        },    
        normalShipping: function()
        {
            if(this.product.shippings.length)
            {
                return this.product.shippings.find(x=> x.type == NORMAL_SHIPPING);
            }
            return null;
        },
        expressShipping: function()
        {
            return this.product && this.product.shippings ? this.product.shippings.find(x=> x.type == EXPRESS_SHIPPING) : null;
        },
        supportedLanguages: function()
        {
            return CONSTANTS.supportedLanguages;
        },
    },
    methods:{
        ...mapActions('palettes', ['getPalettes']),
        ...mapActions('categories', ['getInflateCategories']),
        ...mapActions('categories', ['getActiveCategories']),
        genericValidator,
        errorValidator,
        resetProduct(){
            if(!this.baseProduct)
            {
                var names = {}; var shortDescriptions = {}; var longDescriptions = {}; var packageDimensions = {}; var tags = {}; var datasheets = {};
                CONSTANTS.supportedLanguages.forEach(language => { 
                    names[language.key] = ""; 
                    shortDescriptions[language.key] = ""; 
                    longDescriptions[language.key] = ""; 
                    packageDimensions[language.key] = ""; 
                    tags[language.key] = []; 
                    datasheets[language.key] = { url: "" }; 
                });
                var shippings = [{type: NORMAL_SHIPPING}];
                var defaultTax = 'Free';

                this.product = new Product(names, shortDescriptions, longDescriptions, packageDimensions, tags, datasheets, shippings, [], [], defaultTax);
                this.product.showFirst = 999;
            }
            else
            {
                this.product = deepClone(new Product, this.baseProduct);  

                // if we add a product without Ecommerce the tax comes == 0
                if(this.product.tax === 0) this.product.tax = "Free"

                var hydrateGallery = function(paths)
                {
                    return (paths || []).map(function(file) { return { url: API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, file]) }; });
                };
                this.product.files = hydrateGallery(this.product.files);
                this.product.activeBrandsProFiles = hydrateGallery(this.product.activeBrandsProFiles);

                this.product.modelFile = this.product.modelPath;
                this.product.mtlFile = this.product.mtlPath;
                this.product.svgFile = this.product.svgPath;
            
                this.product.categories = this.product.categories.map(x=> x.id);

                // Ensure datasheets have proper structure for all languages
                CONSTANTS.supportedLanguages.forEach(language => {
                    if (!this.product.datasheets[language.key]) {
                        this.product.datasheets[language.key] = { url: "" };
                    } else if (typeof this.product.datasheets[language.key] === 'string') {
                        this.product.datasheets[language.key] = { url: this.product.datasheets[language.key] };
                    }
                });
            }
        },
        getAllPalettes()
        {
            const itemsPerPage = 999;
            const currentPage = 1;
            const searchText = '';
            const sorterValue = 1; // Ascending
            const propertyName = "Name";

            let input = new InputGetList(itemsPerPage, currentPage, searchText, sorterValue, propertyName);

            this.getPalettes(input); 
        },
        submitClicked(){
            this.attemptedSubmit = true;

            // Datasheets validation: if any language has a PDF, all must have
            const datasheets = this.product.datasheets;
            const languages = this.languages || this.supportedLanguages;
            const filled = languages.filter(lang => {
                const ds = datasheets[lang.key];
                return ds && (
                    (ds.url && ds.url.trim() !== "") ||
                    (ds.data && ds.data.trim() !== "")
                );
            });
            if (filled.length > 0 && filled.length < languages.length) {
                alert(this.$t('datasheetsAllLanguagesRequired'));
                return;
            }

            // Ensure each datasheet has only url and mime properties
            for (const lang of languages) {
                const key = lang.key;
                const ds = datasheets[key] || {};
                let url = ds.url || "";
                let mime = ds.mime || "file/pdf";
                if (ds.data) {
                    url = `data:file/pdf;base64,${ds.data}`;
                }
                datasheets[key] = {
                    url,
                    mime
                };
            }

            // Emit event
            if(this.product.isValid())
            {
                this.submitting = true;

                // uppdate product - When we update product without model or when we remove the file, we need to pass in model,mtl,svg and paletteId response to null
                if(this.product.model == false || this.product.modelFile == null || this.product.svgFile == null || this.product.mtlFile == null)
                {
                    this.product.modelPath = null;
                    this.product.svgPath = null;
                    this.product.mtlPath = null;
                    this.product.paletteId = null;
                }
                
                // Use a clone to modify paths
                var inputProduct = deepClone(new Product, this.product);

                if (typeof inputProduct.modelFile === 'string' || inputProduct.modelFile instanceof String)
                {
                    inputProduct.modelFile = { url: inputProduct.modelFile };
                }

                if (typeof inputProduct.mtlFile === 'string' || inputProduct.mtlFile instanceof String)
                {
                    inputProduct.mtlFile = { url: inputProduct.mtlFile };
                }

                if (typeof inputProduct.svgFile === 'string' || inputProduct.svgFile instanceof String)
                {
                    inputProduct.svgFile = { url: inputProduct.svgFile };
                }

                this.$emit('submit-requested', inputProduct);
            }
        },
        onClickConfirmationPopup(value){
            this.showConfirmationPopup = value;     
            router.push({name: "ProductsList"});
        },
        onProcessTask: function(val)
        {
            if(val != null)
            {
                if(val)
                {
                    this.showConfirmationPopup = true;
                }

                this.submitting = false;
            }
        },
        addPrice: function()
        {
            var price = new Price();

            if(!this.product.prices.length)
            {
                price.minQuantity = 2;
            }

            price.maxQuantity = null;
            this.product.prices.push(price);
        },
        removePrice: function(index)
        {
            this.product.prices.splice(index, 1);
        },
        addExpress: function()
        {
            this.product.shippings.push({type: EXPRESS_SHIPPING});
        },
        removeExpress: function()
        {
            var express = this.product.shippings.find(x=> x.type == EXPRESS_SHIPPING);
            var index = this.product.shippings.indexOf(express);
            this.product.shippings.splice(index, 1);
        },
        ensureNormalShipping: function()
        {
            if (!this.normalShipping) {
                this.product.shippings.push({type: NORMAL_SHIPPING});
            }
        }
    },
    watch:
    {     
        putProductOutput: function(val)
        {
            this.onProcessTask(val);
        },
        postProductOutput: function(val)
        {
            this.onProcessTask(val);
        },
        baseProduct: function()
        {
            this.resetProduct();
        },
        alertType: function()
        {
            this.submitting = false;
        },
        alertOutput: function()
        {
            this.submitting = false;

            // Show pop up in case it is a string error
            // In case it is an array, it is shown on the field
            if(typeof this.alertOutput === 'string' || this.alertOutput instanceof String)
            {
                alert(this.alertOutput);
            }
        }       
    }
  }
</script> 

<style scoped>
.btn-secondary
{
    height: 25px;
    padding: 0 0.5rem;
}
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.2s ease;
}
.fade-enter, .fade-leave-to {
    opacity: 0;
}
</style>

<style>
.vue-tags-input.is-invalid{
    border: 1px solid #e55353;
}
.vue-tags-input{
    max-width: unset;
}
</style>